import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useFeatures } from './featureContext';
import { ReminderList } from './ui/tasks';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  FileText, Receipt, Banknote, CheckSquare, ArrowRight, AlertCircle, CheckCircle2,
  Calculator, ShoppingCart, Users, Landmark, ChevronRight, ChevronDown, PieChart, Target,
} from 'lucide-react';
import { fmtMoney, money } from './money';

const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };
const DONUT_COLORS = ['#059669', '#0ea5e9', '#8b5cf6', '#f59e0b', '#f43f5e', '#14b8a6'];

// App chips row — links to each app's landing view (labels in one map, no hardcoded data).
const APP_CHIPS = [
  ['Accounting', Calculator, 'coa'],
  ['Expenses & Pay Bills', Receipt, 'expenses_overview'],
  ['Sales & Get Paid', ShoppingCart, 'sales_overview', 'invoices'],
  ['Customer Hub', Users, 'customerhub_overview', 'invoices'],
  ['Business Tax', FileText, 'biztax_overview', 'year_end'],
];

function Card({ title, icon: Icon, right, children, testid, card }) {
  return (
    <div className={`rounded-xl border ${card} p-4`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-slate-500 text-sm">{Icon && <Icon className="h-4 w-4" />}{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function Home({ base, isDark = true, firstName = 'Treasurer', go, orgId }) {
  const feat = useFeatures();
  const [d, setD] = useState(null);
  const [rvGranted, setRvGranted] = useState(null);
  const [syncIssues, setSyncIssues] = useState(0);
  const [cashMode, setCashMode] = useState('balance'); // 'balance' | 'flow'
  const [bva, setBva] = useState(null); // Budget vs. Actuals (AD Finance budget, budget bridge)
  useEffect(() => {
    let dead = false;
    axios.get(`${base}/accounting/home`).then((r) => { if (!dead) setD(r.data); }).catch(() => { if (!dead) setD(null); });
    axios.post(`${base}/reports/run`, { report: 'budget_vs_actual', params: {} }).then((r) => { if (!dead) setBva(r.data?.budget ? r.data : null); }).catch(() => { if (!dead) setBva(null); });
    (async () => {
      try { const root = base.split('/companies/')[0]; const act = (await axios.get(`${root}/ol-activity`)).data; if (!dead) setSyncIssues(act?.count || 0); } catch { /* noop */ }
      try { if (orgId) { const root = base.split('/companies/')[0]; const g = (await axios.get(`${root}/report-viewer`, { params: { org_id: orgId } })).data; if (!dead) setRvGranted(!!g?.enabled); } } catch { /* noop */ }
    })();
    return () => { dead = true; };
  }, [base, orgId]);
  const toggleRv = async () => { const root = base.split('/companies/')[0]; const next = !rvGranted; try { await axios.put(`${root}/report-viewer`, { org_id: orgId, enabled: next }); setRvGranted(next); } catch { /* noop */ } };

  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const pnl = d?.profit_loss; const exp = d?.expenses; const banks = d?.bank_accounts; const sf = d?.sales_funnel; const cf = d?.cash_flow;
  const netNoun = pnl?.is_nonprofit ? 'Net revenue' : 'Net profit';
  const donutTotal = (exp?.donut || []).reduce((s, x) => s + (x.amount || 0), 0);
  const cashSeries = useMemo(() => (cf?.series || []).map((p) => ({ month: p.month, value: (cashMode === 'balance' ? p.balance : (p.in - p.out)) / 100 })), [cf, cashMode]);

  const createChips = [
    ['Create invoice', FileText, () => go('form', 'invoice'), 'invoices'],
    ['Record expense', Receipt, () => go('form', 'expense')],
    ['Add bank deposit', Banknote, () => go('form', 'deposit')],
    ['Create check', CheckSquare, () => go('form', 'check')],
    ['Show all', ArrowRight, () => go('create')],
  ];

  return (
    <div className="space-y-6" data-testid="olfin-home">
      <h1 className="text-2xl font-bold" data-testid="home-greeting">{greeting()}, {firstName}!</h1>

      {/* App chips */}
      <div className="flex flex-wrap gap-2" data-testid="home-app-chips">
        {APP_CHIPS.filter((c) => !c[3] || feat[c[3]]).map(([label, Icon, view]) => (
          <button key={label} onClick={() => go(view)} data-testid={`home-app-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${card} hover:border-emerald-500 transition-colors`}>
            <Icon className="h-4 w-4 text-emerald-500" />{label}
          </button>
        ))}
      </div>

      {/* Create actions */}
      <div className="flex flex-wrap gap-2" data-testid="home-create-actions">
        {createChips.filter((c) => !c[3] || feat[c[3]]).map(([label, Icon, onClick]) => (
          <button key={label} onClick={onClick} data-testid={`home-chip-${label.toLowerCase().replace(/\s/g, '-')}`}
            className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-500 hover:bg-emerald-500/20 transition-colors">
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {syncIssues > 0 && (
        <div data-testid="home-sync-banner" className={`rounded-xl border p-3 ${isDark ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'}`}>
          <span className="text-sm font-medium text-amber-600">{syncIssues} OmniLeagues sync event(s) need attention. Open “My accountant / OL activity”.</span>
        </div>
      )}

      <h2 className="text-lg font-semibold">Business at a glance</h2>
      {!d ? <div className="text-slate-400 text-sm" data-testid="home-loading">Loading…</div> : (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {/* NEEDS ATTENTION */}
        <Card title="Needs attention" icon={AlertCircle} testid="home-card-needs" card={card}>
          {(d.needs_attention || 0) > 0 ? (
            <>
              <div className="text-3xl font-bold">{d.needs_attention}</div>
              <p className="text-xs text-slate-500 mt-1 mb-2">reminder(s) and to-do(s)</p>
              <ReminderList rows={d.reminders} go={go} compact />
              <button className="text-emerald-600 text-sm mt-3 hover:underline" onClick={() => go('tasks')} data-testid="home-needs-go">See all</button>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-500"><CheckCircle2 className="h-5 w-5 text-emerald-500" />You&apos;re all caught up
              {<button className="ml-1 text-emerald-600 hover:underline" onClick={() => go('tasks')} data-testid="home-needs-task">Create a task</button>}</div>
          )}
        </Card>

        {/* PROFIT & LOSS */}
        <Card title="Profit &amp; Loss" icon={PieChart} testid="home-card-pnl" card={card}
          right={<span className="text-xs text-slate-400 flex items-center gap-1">Last month <ChevronDown className="h-3 w-3" /></span>}>
          {pnl?.has_data ? (
            <>
              <div className={`text-2xl font-bold tabular-nums ${pnl.net < 0 ? 'text-rose-500' : ''}`}>{fmtMoney(pnl.net)}</div>
              <p className="text-xs text-slate-500 mt-0.5">{netNoun} for {pnl.month_label}</p>
              <div className="mt-3 space-y-1.5">
                {[['Income', pnl.income, 'bg-emerald-500'], ['Expenses', pnl.expenses, 'bg-rose-400']].map(([l, v, c]) => (
                  <div key={l}><div className="flex justify-between text-xs"><span>{l}</span><span className="tabular-nums">{fmtMoney(v)}</span></div>
                    <div className="h-2 rounded-full bg-slate-200/40"><div className={`h-2 rounded-full ${c}`} style={{ width: `${Math.min(100, (Math.abs(v) / Math.max(1, pnl.income, pnl.expenses)) * 100)}%` }} /></div></div>
                ))}
              </div>
              <button className="text-emerald-600 text-sm mt-3 hover:underline" onClick={() => go('reports')} data-testid="home-pnl-go">Analyze</button>
            </>
          ) : <p className="text-sm text-slate-500" data-testid="home-pnl-empty">Chart appears once data is available.</p>}
        </Card>

        {/* EXPENSES */}
        <Card title="Expenses" icon={Receipt} testid="home-card-expenses" card={card}
          right={<span className="text-xs text-slate-400 flex items-center gap-1">Last 30 days <ChevronDown className="h-3 w-3" /></span>}>
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-bold tabular-nums">{fmtMoney(exp?.spend_30 || 0)}</div>
            {exp?.pct_change != null && <span className={`text-xs ${exp.pct_change > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>{exp.pct_change > 0 ? '+' : ''}{exp.pct_change}% vs prior 30 days</span>}
          </div>
          {donutTotal > 0 ? (
            <div className="mt-3 flex items-center gap-4">
              <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                {(() => { let acc = 0; return (exp.donut || []).map((seg, i) => { const frac = seg.amount / donutTotal; const dash = frac * 100; const el = (
                  <circle key={i} cx="18" cy="18" r="15.9155" fill="transparent" stroke={DONUT_COLORS[i % DONUT_COLORS.length]} strokeWidth="4"
                    strokeDasharray={`${dash} ${100 - dash}`} strokeDashoffset={-acc} />); acc += dash; return el; }); })()}
              </svg>
              <div className="space-y-0.5 flex-1">
                {(exp.donut || []).slice(0, 4).map((seg, i) => (
                  <div key={seg.name} className="flex items-center justify-between text-xs"><span className="flex items-center gap-1 truncate"><span className="inline-block h-2 w-2 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />{seg.name}</span><span className="tabular-nums">{fmtMoney(seg.amount)}</span></div>
                ))}
              </div>
            </div>
          ) : <p className="text-xs text-slate-500 mt-2">No spending in the last 30 days.</p>}
          <button className="text-emerald-600 text-sm mt-3 hover:underline" onClick={() => go('bankreview')} data-testid="home-exp-review">
            {(exp?.review_count || 0)} to review: {fmtMoney(exp?.review_amount || 0)} · Categorize {exp?.review_count || 0} transactions</button>
        </Card>

        {/* BANK ACCOUNTS */}
        <Card title="Bank accounts" icon={Landmark} testid="home-card-banks" card={card}
          right={<span className="text-xs text-slate-400">As of today</span>}>
          <div className="text-xs text-slate-500">Total bank balance</div>
          <div className="text-2xl font-bold tabular-nums mb-3">{banks?.total_statement_balance != null ? fmtMoney(banks.total_statement_balance) : <span className="text-slate-400 text-base">Not set</span>}</div>
          <div className="space-y-2">
            {(banks?.accounts || []).slice(0, 5).map((a) => (
              <div key={a.account_id} className="text-sm">
                <div className="flex justify-between"><span className="truncate">{a.name}</span><span className="tabular-nums font-medium">{a.statement_balance != null ? fmtMoney(a.statement_balance) : '—'}</span></div>
                <div className="text-[11px] text-slate-500">In OL Finance {fmtMoney(a.book_balance || 0)}{(a.to_review || 0) > 0 ? ` · ${a.to_review} to review` : ''}</div>
              </div>
            ))}
            {(banks?.accounts || []).length === 0 && <p className="text-xs text-slate-500">No bank or credit card accounts.</p>}
          </div>
          <button className="text-emerald-600 text-sm mt-3 hover:underline flex items-center gap-1" onClick={() => go('banking', 'register')} data-testid="home-banks-go">Go to registers <ChevronDown className="h-3 w-3" /></button>
        </Card>

        {/* SALES & GET PAID FUNNEL (optional feature: invoices) */}
        {feat.invoices && <Card title="Sales &amp; Get Paid" icon={ShoppingCart} testid="home-card-sales" card={card}>
          <div className="flex items-stretch gap-1">
            {[['Not Paid', sf?.not_paid?.amount || 0, `${sf?.not_paid?.count || 0} open`], ['Paid', sf?.paid?.amount || 0, 'last 30 days'], ['Deposited', sf?.deposited?.amount || 0, 'last 30 days']].map(([l, amt, sub], i, arr) => (
              <React.Fragment key={l}>
                <div className="flex-1 rounded-lg bg-slate-500/5 border border-slate-500/20 p-2.5" data-testid={`home-sales-${l.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="text-[11px] text-slate-500">{l}</div><div className="text-sm font-semibold tabular-nums">{fmtMoney(amt)}</div><div className="text-[10px] text-slate-400">{sub}</div>
                </div>
                {i < arr.length - 1 && <div className="flex items-center text-slate-300"><ChevronRight className="h-4 w-4" /></div>}
              </React.Fragment>
            ))}
          </div>
          <button className="text-emerald-600 text-sm mt-3 hover:underline" onClick={() => go('sales_overview')} data-testid="home-sales-go">View sales</button>
        </Card>}

        {/* CASH FLOW */}
        <Card title="Cash flow" icon={Banknote} testid="home-card-cashflow" card={card}
          right={<span className="text-xs text-slate-400 flex items-center gap-1">12 months <ChevronDown className="h-3 w-3" /></span>}>
          <div className="flex items-baseline justify-between">
            <div><div className="text-2xl font-bold tabular-nums">{fmtMoney(cf?.today_cash || 0)}</div><p className="text-xs text-slate-500">Cash balance today</p></div>
            <div className="flex rounded-full border border-slate-500/30 overflow-hidden text-[11px]">
              {[['balance', 'Cash balance'], ['flow', 'Money in/out']].map(([m, l]) => (
                <button key={m} onClick={() => setCashMode(m)} data-testid={`home-cash-mode-${m}`} className={`px-2.5 py-1 ${cashMode === m ? 'bg-emerald-600 text-white' : 'text-slate-400'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ width: '100%', height: 160 }} className="mt-2">
            <ResponsiveContainer>
              <AreaChart data={cashSeries} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
                <defs><linearGradient id="cashg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#059669" stopOpacity={0.4} /><stop offset="100%" stopColor="#059669" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f033" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} width={44} />
                <Tooltip formatter={(v) => money(Math.round(v * 100))} />
                <Area type="monotone" dataKey="value" stroke="#059669" strokeWidth={2} fill="url(#cashg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <button className="text-emerald-600 text-sm mt-1 hover:underline" onClick={() => go('reports', null, { report: 'cash_flows' })} data-testid="home-cash-go">View cash flow</button>
        </Card>

        {/* BUDGET vs ACTUAL — the AD Finance budget measured against these books (shown only when a budget exists) */}
        {bva && (() => { const n = (bva.net || [])[bva.net.length - 1] || { actual: 0, budget: 0 }; return (
          <Card title="Budget vs. actual" icon={Target} testid="home-card-budget" card={card}
            right={<span className="text-xs text-slate-400">{bva.budget.name}</span>}>
            <div className="flex items-baseline gap-4">
              <div><div className="text-2xl font-bold tabular-nums">{fmtMoney(n.actual)}</div><p className="text-xs text-slate-500">{netNoun} so far</p></div>
              <div><div className="text-lg font-semibold tabular-nums text-slate-500">{fmtMoney(n.budget)}</div><p className="text-xs text-slate-500">Budgeted for the year</p></div>
            </div>
            <p className={`text-sm mt-2 ${bva.alerts ? 'text-red-500' : 'text-slate-500'}`} data-testid="home-budget-alerts">
              {bva.alerts ? `${bva.alerts} line${bva.alerts > 1 ? 's' : ''} more than ${bva.alert_pct}% past budget` : 'No lines past budget'}</p>
            <button className="text-emerald-600 text-sm mt-1 hover:underline" onClick={() => go('reports', null, { report: 'budget_vs_actual' })} data-testid="home-budget-go">View Budget vs. Actuals</button>
          </Card>); })()}
      </div>
      )}

      {/* OL Manage users panel (kept) */}
      {rvGranted !== null && orgId && (
        <div data-testid="home-finance-access" className={`rounded-xl border p-3 flex items-center justify-between ${card}`}>
          <div>
            <div className="text-sm font-semibold">Manage users · Accountants &amp; Report Viewer</div>
            <div className="text-xs text-slate-500">Invite an Accountant from Member Management. Let the Athletic Director view read-only Finance reports.</div>
          </div>
          <button data-testid="home-rv-toggle" onClick={toggleRv} className={`text-xs px-3 py-1.5 rounded-full border ${rvGranted ? 'bg-emerald-600 text-white border-emerald-600' : (isDark ? 'border-slate-600' : 'border-slate-300')}`}>
            AD Report Viewer: {rvGranted ? 'On' : 'Off'}
          </button>
        </div>
      )}
    </div>
  );
}
